import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DownloadIcon from '@mui/icons-material/Download';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import * as XLSX from 'xlsx';

/**
 * Look up comparison status for a row from the comparisonMap.
 * Mirrors getComparisonStatus in GLExportTable.
 */
const getRowComparison = (row, comparisonMap) => {
  if (!comparisonMap) return null;
  const debit = Number(row.debitamount) || 0;
  const direction = debit > 0 ? 'debit' : 'credit';
  const key = `${row.transactionid}|${row.memodetail}|${direction}`;
  return comparisonMap[key] || null;
};

const GLExportDialog = ({
  open,
  onClose,
  firebaseUser,
  initialStartDate,
  initialEndDate,
  initialGroupBy = 'store',
  data = [], // Current table data to extract available accounts
  comparisonData = null,
}) => {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [groupBy, setGroupBy] = useState(initialGroupBy);
  const [exportType, setExportType] = useState('categorized'); // 'categorized' or 'granular'
  const [selectedAccounts, setSelectedAccounts] = useState([]); // Array of selected accounts (max 6 for granular)
  const [selectedStore, setSelectedStore] = useState(null); // null = all stores (only for 'store' groupBy)
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [includeComparison, setIncludeComparison] = useState(!!comparisonData);

  // Update state when dialog opens with new initial values
  React.useEffect(() => {
    if (open) {
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
      setGroupBy(initialGroupBy);
      setSelectedAccounts([]);
      setSelectedStore(null);
      setError(null);
      setIncludeComparison(!!comparisonData);
    }
  }, [open, initialStartDate, initialEndDate, initialGroupBy, comparisonData]);

  // Extract unique account + memodetail combinations from data
  const availableAccounts = useMemo(() => {
    const accounts = new Map();
    data.forEach((row) => {
      if (row.accountnumber && row.memodetail) {
        // Use accountnumber + memodetail as the unique key
        const key = `${row.accountnumber}|${row.memodetail}`;
        if (!accounts.has(key)) {
          accounts.set(key, {
            accountnumber: row.accountnumber,
            memodetail: row.memodetail,
            key: key,
            label: `${row.accountnumber} - ${row.memodetail.replace(/^\d+\s*-?\s*/, '')}`,
          });
        }
      }
    });
    return Array.from(accounts.values()).sort((a, b) => {
      // Sort by account number first, then by memodetail
      const acctCompare = a.accountnumber.localeCompare(b.accountnumber);
      if (acctCompare !== 0) return acctCompare;
      return a.memodetail.localeCompare(b.memodetail);
    });
  }, [data]);

  // Extract unique stores from data
  const availableStores = useMemo(() => {
    const stores = new Set();
    data.forEach((row) => {
      if (row.locationnumber && row.locationnumber !== 'ALL') {
        stores.add(row.locationnumber);
      }
    });
    return Array.from(stores).sort((a, b) => parseInt(a) - parseInt(b));
  }, [data]);

  /**
   * Build a filename for the export.
   */
  const buildFilename = (suffix = '') => {
    const dateRange = `${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}`;
    const accountSuffix = selectedAccounts.length > 0 ? `-${selectedAccounts.length}accts` : '';
    const storeSuffix = groupBy === 'store' && selectedStore ? `-store${selectedStore}` : '';
    const typeSuffix = exportType === 'granular' ? '-details' : '';
    return `gl-export${accountSuffix}${storeSuffix}${typeSuffix}${suffix}-${dateRange}.xlsx`;
  };

  /**
   * Generate an XLSX client-side from the current table data + comparison columns.
   */
  const exportWithComparison = () => {
    // Apply the same account/store filters the dialog offers
    let rows = [...data];

    if (selectedAccounts.length > 0) {
      const keys = new Set(selectedAccounts.map((a) => `${a.accountnumber}|${a.memodetail}`));
      rows = rows.filter((r) => keys.has(`${r.accountnumber}|${r.memodetail}`));
    }
    if (groupBy === 'store' && selectedStore) {
      rows = rows.filter((r) => r.locationnumber === selectedStore);
    }

    const compMap = comparisonData?.comparisonMap;

    const sheetData = rows.map((row) => {
      const comparison = getRowComparison(row, compMap);
      const debit = Number(row.debitamount) || 0;
      const credit = Number(row.creditamount) || 0;
      // SF Net is always computable from the row itself
      const sfNet = comparison ? (comparison.snowflakeAmount ?? (debit > 0 ? debit : credit)) : null;
      // NS Net: explicit on variance rows, equals SF on match, null on missing
      const nsNet = comparison
        ? comparison.status === 'variance'
          ? comparison.netsuiteAmount
          : comparison.status === 'match'
            ? sfNet
            : null
        : null;
      const variance = comparison?.status === 'variance' ? comparison.difference : null;

      const out = {
        'Transaction ID': row.transactionid ?? '',
        'Transaction Date': row.transactiondate ?? '',
        'Subsidiary ID': row.subsidiaryid ?? '',
        'Location': row.locationnumber ?? '',
        'Account': row.accountnumber ?? '',
        'Debit': row.debitamount != null ? Number(row.debitamount) : '',
        'Credit': row.creditamount != null ? Number(row.creditamount) : '',
        'SF Net': sfNet != null ? Number(sfNet) : '',
        'NS Net': nsNet != null ? Number(nsNet) : '',
        'Variance': variance != null ? Number(variance) : '',
        'Memo Header': row.memoheader ?? '',
        'Memo Detail': row.memodetail ?? '',
        'Comparison': comparison?.status ?? '',
      };
      return out;
    });

    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'GL Export');
    XLSX.writeFile(wb, buildFilename('-comparison'));
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Please select a date range');
      return;
    }

    // For granular export, require at least 1 account
    if (exportType === 'granular' && selectedAccounts.length === 0) {
      setError('Please select at least 1 account for granular export');
      return;
    }

    // When comparison data is included and export is categorized,
    // generate the XLSX client-side so the comparison columns are included.
    if (includeComparison && comparisonData && exportType === 'categorized') {
      try {
        setExporting(true);
        setError(null);
        exportWithComparison();
        onClose();
      } catch (err) {
        console.error('Error exporting GL data with comparison:', err);
        setError('Failed to export data. Please try again.');
      } finally {
        setExporting(false);
      }
      return;
    }

    try {
      setExporting(true);
      setError(null);
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      // Build query parameters
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        groupBy,
        exportType,
      });

      // Add selected account/memodetail combinations (multiple)
      if (selectedAccounts.length > 0) {
        selectedAccounts.forEach((acct) => {
          params.append('accountNumbers', acct.accountnumber);
          params.append('memoDetails', acct.memodetail);
        });
      }

      if (groupBy === 'store' && selectedStore) {
        params.append('storeId', selectedStore);
      }

      const response = await axios.get(`/hd-api/gl-export/export-flexible?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
        responseType: 'blob',
        timeout: 300000, // 5 minute timeout
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = buildFilename();

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      onClose();
    } catch (err) {
      console.error('Error exporting GL data:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export GL Data</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Date Range */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Date Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Start Date"
                customInput={
                  <TextField size="small" label="Start Date" sx={{ flex: 1 }} autoComplete="off" />
                }
              />
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText="End Date"
                customInput={
                  <TextField size="small" label="End Date" sx={{ flex: 1 }} autoComplete="off" />
                }
              />
            </Box>
          </Box>

          {/* Group By */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Grouping
            </Typography>
            <ToggleButtonGroup
              value={groupBy}
              exclusive
              onChange={(e, val) => val && setGroupBy(val)}
              size="small"
              fullWidth
            >
              <ToggleButton value="store">
                <StorefrontIcon sx={{ mr: 1, fontSize: 18 }} />
                By Store
              </ToggleButton>
              <ToggleButton value="company">
                <BusinessIcon sx={{ mr: 1, fontSize: 18 }} />
                Company-Wide
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Store Filter (only when groupBy is 'store') */}
          {groupBy === 'store' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Store (Optional)
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel>All Stores</InputLabel>
                <Select
                  value={selectedStore || ''}
                  onChange={(e) => setSelectedStore(e.target.value || null)}
                  label="All Stores"
                >
                  <MenuItem value="">
                    <em>All Stores</em>
                  </MenuItem>
                  {availableStores.map((store) => (
                    <MenuItem key={store} value={store}>
                      Store {store}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Account/Memo Detail Filter */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Account or Memo Detail{exportType === 'granular' ? ' (Required)' : ' (Optional)'}
            </Typography>
            <Autocomplete
              multiple
              value={selectedAccounts}
              onChange={(e, val) => {
                // Limit to 6 selections max
                if (val.length <= 6) {
                  setSelectedAccounts(val);
                }
              }}
              options={availableAccounts}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              getOptionDisabled={() => selectedAccounts.length >= 6}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={selectedAccounts.length === 0 ? 'Select account or memo detail...' : ''}
                  error={exportType === 'granular' && selectedAccounts.length === 0}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  <Box>
                    <Typography variant="body2">{option.accountnumber}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.memodetail}
                    </Typography>
                  </Box>
                </li>
              )}
              ChipProps={{ size: 'small' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {selectedAccounts.length}/6 selected
              {exportType === 'granular' && selectedAccounts.length === 0 && (
                <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                  (At least 1 required)
                </Typography>
              )}
            </Typography>
          </Box>

          {/* Export Type */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Export Type
            </Typography>
            <ToggleButtonGroup
              value={exportType}
              exclusive
              onChange={(e, val) => val && setExportType(val)}
              size="small"
              fullWidth
            >
              <ToggleButton value="categorized">
                <TableChartIcon sx={{ mr: 1, fontSize: 18 }} />
                Categorized (Summary)
              </ToggleButton>
              <ToggleButton value="granular">
                <ListAltIcon sx={{ mr: 1, fontSize: 18 }} />
                Granular (Details)
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {exportType === 'categorized'
                ? 'Export aggregated data as shown in the main table'
                : 'Export individual transaction details (like drill-down view)'}
            </Typography>
          </Box>

          {/* Include NetSuite Comparison */}
          {exportType === 'categorized' && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeComparison}
                    onChange={(e) => setIncludeComparison(e.target.checked)}
                    disabled={!comparisonData}
                  />
                }
                label="Include NetSuite comparison data"
              />
              {!comparisonData && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  Run "Compare to NetSuite" first to enable this option
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={exporting || !startDate || !endDate || (exportType === 'granular' && selectedAccounts.length === 0)}
          startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GLExportDialog;
